/**
 * Global permission directive
 * Used for fine-grained control of component permissions
 * @Example v-auth="RoleEnum.ADMIN"
 * @Example v-auth="[RoleEnum.ADMIN, UserRoleEnum.ACCOUNT_MANAGER]"
 * @Example v-auth.role="[RoleEnum.ADMIN]"
 * @Example v-auth.computed="'isAnyMsspManager'"
 * @Example v-auth.permission="[PermissionEnum.ADD_ASSET]"
 * @Example v-auth.permission.disable="[PermissionEnum.ADD_ASSET]"
 * @Example v-auth.permission.not.disable="[PermissionEnum.ADD_ASSET]"
 * @Example v-auth.module="[BRM]"
 *
 * Do not use this directive when you want child components not to be compiled at all.
 * Because Vue calls child component and run their code.
 * If you don't want to this, use v-if
 * This directive useful with html elements like button, input.
 */
import type { Directive, DirectiveBinding, DirectiveHook } from 'vue'

import isUndefined from 'lodash-es/isUndefined'

type Behaviour = 'disable' | 'readonly' | 'hide'

function getBehaviour(modifiers: any): Behaviour {
  if (!isUndefined(modifiers.disable)) {
    return 'disable'
  }
  if (!isUndefined(modifiers.readonly)) {
    return 'readonly'
  }
  return 'hide'
}

const onMounted: DirectiveHook = (el: Element, binding: DirectiveBinding<any>) => {
  // if value is not defined, do nothing.
  const value = binding.value
  if (!value) return

  // It can be permission or role, default value ise role
  const arg = (binding.arg || 'role') as string
  const not = binding.modifiers.not
  const behaviour: Behaviour = getBehaviour(binding.modifiers)

  const { hasRole, hasPermission, hasModule } = useUserPermission()

  let ok = false
  // Check for role v-can.role=[]
  if (arg === 'role') {
    ok = hasRole(value)
  }
  // Check for permission v-can.permission=[]
  if (arg === 'permission') {
    ok = hasPermission(value)
  }
  // Check for module v-can.modules=[]
  if (arg === 'module') {
    ok = hasModule(value)
  }

  // Check for computed value v-can.computed="'isAny...'"
  if (arg === 'computed') {
    const permissions: Recordable = useUserPermission()
    ok = permissions[value].value
  }

  /**
   * It works 2 ways.
   * 1. User has permission or role for this operation but `not modifier` given.
   * So it means reverse behaviour.
   * 2. User hasn't permission or role and `not modifier` given.
   */
  if ((ok && not) || (!ok && !not)) {
    if (behaviour === 'hide') {
      el.parentNode?.removeChild(el)
    } else if (behaviour === 'disable') {
      el.setAttribute('disabled', 'disabled')
    } else if (behaviour === 'readonly') {
      el.setAttribute('readonly', 'readonly')
    }
  }
}

export const vAuth: Directive = {
  mounted: onMounted,
}
